/**
 * Riftgate Arena — the i18n runtime: one active locale, one `t()`.
 *
 * `translate.ts` decides what a string says; this file decides WHICH string,
 * owns the single mutable locale, and is the only part that touches the
 * network, `localStorage` and `<html>`.
 *
 * ── THE FALLBACK CHAIN, AND WHY IT NEVER ENDS IN A BLANK ────────────────────
 *      active locale  ->  default locale (en)  ->  the key itself
 * An untranslated key renders as `settings.section_audio`, which is ugly on
 * purpose: it is visible in a screenshot, greppable in a bug report and
 * impossible to mistake for finished work. A blank string is none of those —
 * it looks like a design decision, ships, and is found by a player.
 *
 * A message that exists but is EMPTY is treated as missing (`isBlank`), because
 * that is what a half-finished translation looks like.
 *
 * ── MISSING-KEY POLICY ──────────────────────────────────────────────────────
 *   dev  (`import.meta.env.DEV`)  'warn'   — console.error, once per key
 *   prod                          'silent' — fall back quietly, never blank
 *   opt-in                        'throw'  — for a CI screen-walk that must
 *                                            fail rather than render a key
 * Deduplication is per key+locale: loud once beats loud sixty times a second
 * from a render loop, which is the same as silent because nobody reads it.
 *
 * ── LOADING ─────────────────────────────────────────────────────────────────
 * `initI18n()` awaits ONE catalogue chunk — the active locale — plus the
 * default-locale chunk when they differ, because the fallback above has to have
 * something to fall back TO. Core's `errors`/`emails`/`currencies` namespace is
 * fetched in the BACKGROUND and mounted under `core.`; a slow or dead backend
 * therefore cannot delay first paint, and subscribers are notified when it
 * lands so anything already on screen re-renders.
 *
 * `initI18n()` deliberately never rejects. If the chunk itself fails to load
 * the app still renders — in raw keys, with `snapshot().ready === false` and a
 * loud console error — because a navigable broken UI beats a white screen.
 * `setLocale()` DOES reject: its caller is a settings control that can say so.
 */
import { authStore } from '../../core/state/auth-store';
import { api } from '../../core/network/api';
import {
  DEFAULT_LOCALE,
  isLocale,
  localeDirection,
  localeTag,
  negotiateLocale,
  toCoreLocale,
  fromCoreLocale,
  type Locale,
  type TextDirection,
} from './locales';
import {
  loadGameCatalogue,
  readLocaleMeta,
  type CatalogueLoader,
  type LocaleMeta,
} from './catalogue';
import { coreLocaleEnabled, fetchCoreBundle, type CoreBundleLoader } from './core-bundle';
import {
  interpolate,
  isBlank,
  isPluralForms,
  lookupNode,
  messageParts,
  resolveMessage,
  type Catalogue,
  type MessagePart,
  type TParams,
} from './translate';

/** Where a device-local language choice is remembered. */
export const LOCALE_STORAGE_KEY = 'rga.locale';

/** The namespace core's bundle is mounted under. Reserved — see `rebuild()`. */
export const CORE_NAMESPACE = 'core';

export type MissingKeyPolicy = 'throw' | 'warn' | 'silent';

export class MissingTranslationError extends Error {
  constructor(
    readonly key: string,
    readonly locale: string,
  ) {
    super(`i18n: no translation for "${key}" in "${locale}" or "${DEFAULT_LOCALE}"`);
    this.name = 'MissingTranslationError';
  }
}

/** Where the initial locale came from. Exposed because "why am I in German?" is a real bug report. */
export type LocaleSource = 'option' | 'query' | 'account' | 'storage' | 'navigator' | 'default';

export interface I18nSnapshot {
  readonly locale: Locale;
  /** BCP-47 tag — for `Intl`, `<html lang>` and `hreflang`. */
  readonly tag: string;
  readonly dir: TextDirection;
  /** True once a catalogue is loaded. False means `t()` is returning keys. */
  readonly ready: boolean;
  /** True once core's `errors`/`emails`/`currencies` namespace is mounted. */
  readonly coreReady: boolean;
  /** True when core served a DIFFERENT language than we asked for. */
  readonly coreFellBack: boolean;
  /** Provenance of the active catalogue — `machine` means nobody reviewed it. */
  readonly meta: LocaleMeta;
  readonly source: LocaleSource;
}

/** Minimal `localStorage` shape, so tests can pass a Map-backed stub. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface InitI18nOptions {
  /** Force a locale, skipping negotiation entirely. */
  locale?: Locale;
  /** The account's stored preference (core `xx_YY` or BCP-47). Wins over the device. */
  accountLocale?: string | null;
  /** Override `navigator.languages` (tests). */
  navigatorLanguages?: readonly string[];
  /** Override `location.search` (tests), e.g. `'?lang=ru'`. */
  search?: string;
  /** `null` disables persistence; omitted uses `localStorage` when usable. */
  storage?: StorageLike | null;
  /** Inject a catalogue loader (tests). */
  loadCatalogue?: CatalogueLoader;
  /** Inject a core-bundle loader, or `false` to skip core's namespace entirely. */
  loadCoreBundle?: CoreBundleLoader | false;
  /**
   * Inject the three account-preference services (tests, and any host that
   * already knows the session). Left alone they talk to core's
   * `GET|PATCH /me/preferences` through `api`.
   */
  loadAccountLocale?: () => Promise<string | null>;
  savePreference?: (locale: Locale) => Promise<void>;
  isAuthenticated?: () => boolean;
  /** Override the environment default. */
  missingKeyPolicy?: MissingKeyPolicy;
  /** Write `lang`/`dir` onto `<html>`. Default true when a document exists. */
  applyToDocument?: boolean;
  /** Also load the default-locale catalogue as the fallback target. Default true. */
  preloadDefault?: boolean;
}

export interface SetLocaleOptions {
  /** Remember the choice on this device. Default true. */
  persist?: boolean;
  /** Push the choice to the account when signed in. Default true. */
  remote?: boolean;
}

/* ════════════════════════════════════════════════ injectable services ═══ */

interface Deps {
  loadCatalogue: CatalogueLoader;
  loadCoreBundle: CoreBundleLoader | null;
  storage: StorageLike | null;
  isAuthenticated(): boolean;
  savePreference(locale: Locale): Promise<void>;
  loadAccountLocale(): Promise<string | null>;
  applyToDocument: boolean;
  preloadDefault: boolean;
}

/** `localStorage` throws outright in a locked-down Safari — probe once, never assume. */
function usableLocalStorage(): StorageLike | null {
  try {
    const probe = globalThis.localStorage;
    if (probe === undefined || probe === null) return null;
    const key = '__rga_i18n_probe__';
    probe.setItem(key, '1');
    probe.removeItem(key);
    return probe;
  } catch {
    return null;
  }
}

const defaultSavePreference = async (locale: Locale): Promise<void> => {
  // The template's `PATCH /me/preferences` validates against ITS locale list,
  // which does not contain ru/lv/pt_BR/tr/ja. Asking first turns a 400 with a
  // confusing message into a single explanatory line in the console; the
  // device-local choice has already been saved either way.
  const enabled = await coreLocaleEnabled(locale);
  if (enabled === false) {
    warnOnce(
      `core-locale-unsupported:${locale}`,
      `[i18n] the backend does not enable "${toCoreLocale(locale)}", so the language ` +
        `choice is remembered on this device only. Adding it needs a core change ` +
        `(SUPPORTED_LOCALES + locales/${toCoreLocale(locale)}.json).`,
    );
    return;
  }
  await api.patch('/me/preferences', { locale: toCoreLocale(locale) });
};

const defaultLoadAccountLocale = async (): Promise<string | null> => {
  try {
    const prefs = await api.get<{ locale: string | null }>('/me/preferences');
    return typeof prefs?.locale === 'string' ? prefs.locale : null;
  } catch {
    return null;
  }
};

function freshDeps(): Deps {
  return {
    loadCatalogue: loadGameCatalogue,
    loadCoreBundle: fetchCoreBundle,
    storage: usableLocalStorage(),
    isAuthenticated: () => authStore.get().user !== null,
    savePreference: defaultSavePreference,
    loadAccountLocale: defaultLoadAccountLocale,
    applyToDocument: true,
    preloadDefault: true,
  };
}

let deps: Deps = freshDeps();

/* ═══════════════════════════════════════════════════════════ state ══════ */

let active: Locale = DEFAULT_LOCALE;
let activeSource: LocaleSource = 'default';
let activeCatalogue: Catalogue | null = null;
let defaultCatalogue: Catalogue | null = null;
let coreMessages: Catalogue | null = null;
let coreFellBack = false;
/** `{ ...game, core: coreMessages }` — the single dictionary `t()` reads. */
let mergedActive: Catalogue | null = null;
let mergedDefault: Catalogue | null = null;
let ready = false;
let coreToken = 0;
let corePending: Promise<boolean> | null = null;

let policy: MissingKeyPolicy = import.meta.env.DEV ? 'warn' : 'silent';
let onMissing: ((key: string, locale: Locale) => void) | null = null;
const reported = new Set<string>();

const listeners = new Set<(snapshot: I18nSnapshot) => void>();
const changeListeners = new Set<(locale: Locale) => void>();

let readyResolve: () => void = () => {};
let readyPromise: Promise<void> = new Promise<void>((resolve) => {
  readyResolve = resolve;
});

function warnOnce(id: string, message: string): void {
  if (reported.has(id)) return;
  reported.add(id);
  console.warn(message);
}

/* ═══════════════════════════════════════════════════════ the merge ═════ */

function merge(game: Catalogue | null): Catalogue | null {
  if (game === null) return coreMessages === null ? null : ({ [CORE_NAMESPACE]: coreMessages } as Catalogue);
  if (coreMessages === null) return game;
  if (
    import.meta.env.DEV &&
    Object.prototype.hasOwnProperty.call(game, CORE_NAMESPACE)
  ) {
    warnOnce(
      'core-namespace-collision',
      `[i18n] the game catalogue defines a top-level "${CORE_NAMESPACE}" namespace, ` +
        `which is reserved for the template's bundle and will be shadowed by it.`,
    );
  }
  return { ...game, [CORE_NAMESPACE]: coreMessages };
}

function rebuild(): void {
  mergedActive = merge(activeCatalogue);
  mergedDefault = active === DEFAULT_LOCALE ? mergedActive : merge(defaultCatalogue);
}

/* ═════════════════════════════════════════════════════════ lookup ══════ */

interface Resolution {
  readonly template: string;
  readonly found: boolean;
}

function resolveKey(key: string, params: TParams | undefined): Resolution {
  if (!ready && mergedActive === null) {
    warnOnce(
      'not-initialised',
      '[i18n] t() was called before initI18n() resolved — strings will render as keys. ' +
        'Await initI18n() before the first render.',
    );
  }

  const direct = resolveMessage(mergedActive ?? undefined, key, params, localeTag(active));
  if (direct !== undefined) return { template: direct, found: true };

  if (active !== DEFAULT_LOCALE) {
    const viaDefault = resolveMessage(
      mergedDefault ?? undefined,
      key,
      params,
      localeTag(DEFAULT_LOCALE),
    );
    if (viaDefault !== undefined) {
      report('fallback', key);
      return { template: viaDefault, found: true };
    }
  }

  report('missing', key);
  return { template: key, found: false };
}

function report(kind: 'fallback' | 'missing', key: string): void {
  if (kind === 'missing') {
    onMissing?.(key, active);
    if (policy === 'throw') throw new MissingTranslationError(key, active);
  }
  if (policy === 'silent') return;
  const id = `${kind}:${active}:${key}`;
  if (reported.has(id)) return;
  reported.add(id);
  if (kind === 'missing') {
    console.error(
      `[i18n] MISSING "${key}" — absent from "${active}" and from "${DEFAULT_LOCALE}". ` +
        `Rendering the key.`,
    );
  } else {
    console.error(
      `[i18n] UNTRANSLATED "${key}" — missing from "${active}", showing the ` +
        `"${DEFAULT_LOCALE}" text.`,
    );
  }
}

/* ══════════════════════════════════════════════════ public: reading ════ */

/**
 * Translate `key`, interpolating `{placeholders}` from `params`.
 *
 * `params.count` selects a plural form when the message is a form object, and
 * is interpolated like any other parameter.
 *
 *     t('common.save')
 *     t('shop.contains', { count: 3 })
 *     t('terms.version_note', { version: '2026-09-01', date: '1 Sept 2026' })
 */
export function t(key: string, params?: TParams): string {
  return interpolate(resolveKey(key, params).template, params);
}

/**
 * Translate into PARTS instead of a string, so a sentence containing a link can
 * be one translatable sentence. See `messageParts` in `translate.ts`.
 */
export function tParts(key: string, params?: TParams): MessagePart[] {
  return messageParts(resolveKey(key, params).template, params);
}

/** Is there a usable message for this key (in the active locale or the default)? */
export function has(key: string): boolean {
  const node =
    lookupNode(mergedActive ?? undefined, key) ?? lookupNode(mergedDefault ?? undefined, key);
  if (node === undefined) return false;
  if (typeof node === 'string') return !isBlank(node);
  // A namespace is not a message — see `isPluralForms`.
  return isPluralForms(node);
}

/**
 * Best available message for a failed API call.
 *
 * Order: a translated string for the error CODE, then one for the HTTP STATUS,
 * then the server's own `message`, then a generic line. The server's message is
 * preferred over the generic one on purpose — an accurate sentence in English
 * helps a player more than a translated sentence that says nothing.
 */
export function tError(error: unknown): string {
  if (error !== null && typeof error === 'object') {
    const e = error as { status?: unknown; code?: unknown; message?: unknown; details?: unknown };

    if (e.status === 0) return t('errors.network');

    if (typeof e.code === 'string' && e.code !== '') {
      const key = `errors.code.${errorKeySlug(e.code)}`;
      if (has(key)) return t(key);
    }
    if (typeof e.status === 'number') {
      const key = `errors.http.${e.status}`;
      if (has(key)) return t(key);
    }

    const fromDetails = firstValidationMessage(e.details);
    if (fromDetails !== null) return fromDetails;
    if (typeof e.message === 'string' && e.message.trim() !== '') return e.message;
  }
  return t('errors.generic');
}

/** `Bad Request` / `InternalServerError` / `TOO_MANY` -> `bad_request` etc. */
function errorKeySlug(code: string): string {
  return code
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

/** class-validator answers with `message: string[]`; show the first line. */
function firstValidationMessage(details: unknown): string | null {
  if (details === null || typeof details !== 'object') return null;
  const message = (details as { message?: unknown }).message;
  if (Array.isArray(message)) {
    const first = message.find((entry) => typeof entry === 'string' && entry.trim() !== '');
    return typeof first === 'string' ? first : null;
  }
  return null;
}

/* ══════════════════════════════════════════════════ public: state ══════ */

export function getLocale(): Locale {
  return active;
}

export function getLocaleTag(): string {
  return localeTag(active);
}

export function getDirection(): TextDirection {
  return localeDirection(active);
}

export function isReady(): boolean {
  return ready;
}

/** Resolves once a catalogue is in place (or immediately, if one already is). */
export function whenReady(): Promise<void> {
  return readyPromise;
}

/** Resolves true when core's namespace is mounted, false when it is unavailable. */
export function whenCoreBundleReady(): Promise<boolean> {
  return corePending ?? Promise.resolve(coreMessages !== null);
}

export function snapshot(): I18nSnapshot {
  return {
    locale: active,
    tag: localeTag(active),
    dir: localeDirection(active),
    ready,
    coreReady: coreMessages !== null,
    coreFellBack,
    meta: readLocaleMeta(activeCatalogue),
    source: activeSource,
  };
}

/** Provenance of the active catalogue — `machine` means nobody reviewed it. */
export function localeMeta(): LocaleMeta {
  return readLocaleMeta(activeCatalogue);
}

/**
 * Subscribe to i18n state. Called IMMEDIATELY with the current snapshot (the
 * `createStore` convention in `core/state/store.ts`), then on every locale
 * change and when core's namespace lands. Returns an unsubscribe function.
 */
export function subscribeI18n(listener: (snapshot: I18nSnapshot) => void): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => {
    listeners.delete(listener);
  };
}

/** Locale changes only, never called on subscribe. For "re-render on switch". */
export function onLocaleChange(listener: (locale: Locale) => void): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

function notify(localeChanged: boolean): void {
  const snap = snapshot();
  for (const listener of [...listeners]) listener(snap);
  if (localeChanged) for (const listener of [...changeListeners]) listener(active);
}

/* ═════════════════════════════════════════════════ public: control ═════ */

export function configureI18n(options: {
  missingKeyPolicy?: MissingKeyPolicy;
  onMissingKey?: ((key: string, locale: Locale) => void) | null;
}): void {
  if (options.missingKeyPolicy !== undefined) policy = options.missingKeyPolicy;
  if (options.onMissingKey !== undefined) onMissing = options.onMissingKey;
}

export function getMissingKeyPolicy(): MissingKeyPolicy {
  return policy;
}

/**
 * Resolve which locale we SHOULD be in, without loading anything.
 *
 * Order, highest first:
 *   1. an explicit option           — a caller that already decided
 *   2. `?lang=xx`                   — a shared or QA link, and it sticks
 *   3. the account preference       — follows the player across devices
 *   4. this device's saved choice   — survives being signed out
 *   5. `navigator.languages`        — the browser's ranked list, not just [0]
 *   6. the default locale
 */
export function preferredLocale(options: InitI18nOptions = {}): {
  locale: Locale;
  source: LocaleSource;
} {
  if (options.locale !== undefined && isLocale(options.locale)) {
    return { locale: options.locale, source: 'option' };
  }

  const fromQuery = localeFromQuery(options.search);
  if (fromQuery !== null) return { locale: fromQuery, source: 'query' };

  const fromAccount = fromCoreLocale(options.accountLocale ?? null);
  if (fromAccount !== null) return { locale: fromAccount, source: 'account' };

  const stored = readStoredLocale(options.storage);
  if (stored !== null) return { locale: stored, source: 'storage' };

  const languages =
    options.navigatorLanguages ??
    (globalThis.navigator?.languages as readonly string[] | undefined) ??
    (globalThis.navigator?.language !== undefined ? [globalThis.navigator.language] : []);
  const negotiated = negotiateLocale(languages);
  if (negotiated !== null) return { locale: negotiated, source: 'navigator' };

  return { locale: DEFAULT_LOCALE, source: 'default' };
}

function localeFromQuery(search: string | undefined): Locale | null {
  const raw = search ?? globalThis.location?.search ?? '';
  if (raw === '') return null;
  let value: string | null = null;
  try {
    value = new URLSearchParams(raw).get('lang');
  } catch {
    return null;
  }
  if (value === null) return null;
  // Untrusted input: negotiate it like any other preference, never trust it.
  return negotiateLocale([value]);
}

function readStoredLocale(override?: StorageLike | null): Locale | null {
  const storage = override === undefined ? deps.storage : override;
  if (storage === null || storage === undefined) return null;
  try {
    const value = storage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

function storeLocale(locale: Locale): void {
  try {
    deps.storage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* a full or disabled quota must not break a language switch */
  }
}

/**
 * Boot the runtime. Awaits the active locale's catalogue chunk (plus the
 * default locale's, as the fallback target) and returns the locale in force.
 * Never rejects — see the header note.
 */
export async function initI18n(options: InitI18nOptions = {}): Promise<Locale> {
  if (options.missingKeyPolicy !== undefined) policy = options.missingKeyPolicy;
  if (options.loadCatalogue !== undefined) deps.loadCatalogue = options.loadCatalogue;
  if (options.loadCoreBundle !== undefined) {
    deps.loadCoreBundle = options.loadCoreBundle === false ? null : options.loadCoreBundle;
  }
  if (options.loadAccountLocale !== undefined) deps.loadAccountLocale = options.loadAccountLocale;
  if (options.savePreference !== undefined) deps.savePreference = options.savePreference;
  if (options.isAuthenticated !== undefined) deps.isAuthenticated = options.isAuthenticated;
  if (options.storage !== undefined) deps.storage = options.storage;
  if (options.applyToDocument !== undefined) deps.applyToDocument = options.applyToDocument;
  if (options.preloadDefault !== undefined) deps.preloadDefault = options.preloadDefault;

  const { locale, source } = preferredLocale(options);
  activeSource = source;

  try {
    await activate(locale, { persistLocal: source === 'query' || source === 'account' });
  } catch (error) {
    console.error(
      `[i18n] failed to load the "${locale}" catalogue — the UI will render translation ` +
        `keys until this is fixed.`,
      error,
    );
    active = locale;
    notify(true);
  }
  return active;
}

/**
 * Switch language: load the catalogue, swap it in, remember the choice.
 *
 * Rejects if the catalogue cannot be loaded, leaving the previous language in
 * place — a settings control can then say "could not switch" instead of
 * silently emptying the screen.
 */
export async function setLocale(locale: Locale, options: SetLocaleOptions = {}): Promise<void> {
  if (!isLocale(locale)) throw new Error(`i18n: unknown locale "${String(locale)}"`);
  const persist = options.persist !== false;
  const remote = options.remote !== false;

  if (locale === active && ready) {
    if (persist) storeLocale(locale);
    if (persist && remote) void pushPreference(locale);
    return;
  }

  activeSource = 'option';
  await activate(locale, { persistLocal: persist });
  if (persist && remote) void pushPreference(locale);
}

async function pushPreference(locale: Locale): Promise<void> {
  if (!deps.isAuthenticated()) return;
  try {
    await deps.savePreference(locale);
  } catch {
    warnOnce(
      `preference-save-failed:${locale}`,
      `[i18n] could not save the language preference to the account; it is remembered ` +
        `on this device only.`,
    );
  }
}

async function activate(locale: Locale, opts: { persistLocal: boolean }): Promise<void> {
  const wantsDefault = deps.preloadDefault && locale !== DEFAULT_LOCALE && defaultCatalogue === null;

  const [catalogue, fallback] = await Promise.all([
    deps.loadCatalogue(locale),
    wantsDefault
      ? deps.loadCatalogue(DEFAULT_LOCALE).catch((error: unknown) => {
          // The fallback failing is bad but survivable: the active catalogue is
          // complete, and a key missing from BOTH renders as the key.
          console.error('[i18n] failed to preload the default-locale catalogue', error);
          return null;
        })
      : Promise.resolve(null),
  ]);

  const changed = active !== locale || !ready;
  active = locale;
  activeCatalogue = catalogue;
  if (locale === DEFAULT_LOCALE) defaultCatalogue = catalogue;
  else if (fallback !== null) defaultCatalogue = fallback;

  rebuild();
  ready = true;
  readyResolve();
  applyDocumentAttributes();
  if (opts.persistLocal) storeLocale(locale);
  notify(changed);

  void refreshCoreBundle(locale);
}

function applyDocumentAttributes(): void {
  if (!deps.applyToDocument) return;
  const root = globalThis.document?.documentElement;
  if (root === undefined || root === null) return;
  root.lang = localeTag(active);
  root.dir = localeDirection(active);
  root.setAttribute('data-locale', active);
}

/**
 * Fetch core's namespace for the active locale, in the background.
 *
 * Guarded by a token so a fast language switch cannot let a slow earlier
 * response overwrite a newer one — the classic out-of-order-response bug.
 */
async function refreshCoreBundle(locale: Locale): Promise<boolean> {
  const loader = deps.loadCoreBundle;
  if (loader === null) {
    coreMessages = null;
    coreFellBack = false;
    rebuild();
    return false;
  }
  const token = ++coreToken;
  const pending = (async () => {
    let bundle: Awaited<ReturnType<CoreBundleLoader>> = null;
    try {
      bundle = await loader(locale);
    } catch {
      bundle = null;
    }
    if (token !== coreToken) return coreMessages !== null;
    coreMessages = bundle?.messages ?? null;
    coreFellBack = bundle?.fellBack ?? false;
    rebuild();
    notify(false);
    return coreMessages !== null;
  })();
  corePending = pending;
  return pending;
}

/**
 * Adopt the language stored on the account (`GET /me/preferences`).
 *
 * For the caller that finishes session bootstrap AFTER `initI18n()` has already
 * painted: the account preference outranks the device, so this switches if they
 * differ — and does not push the value back to the server, because it came from
 * there. Returns the locale in force, or null when there is nothing to adopt.
 */
export async function adoptAccountLocale(): Promise<Locale | null> {
  const raw = await deps.loadAccountLocale();
  const locale = fromCoreLocale(raw);
  if (locale === null) return null;
  activeSource = 'account';
  if (locale === active) {
    storeLocale(locale);
    return locale;
  }
  await setLocale(locale, { persist: true, remote: false });
  activeSource = 'account';
  return locale;
}

/* ═════════════════════════════════════════════════════ test support ════ */

/** Full reset: state, caches, deps and policy. Tests only. */
export function resetI18nForTests(): void {
  deps = freshDeps();
  active = DEFAULT_LOCALE;
  activeSource = 'default';
  activeCatalogue = null;
  defaultCatalogue = null;
  coreMessages = null;
  coreFellBack = false;
  mergedActive = null;
  mergedDefault = null;
  ready = false;
  coreToken = 0;
  corePending = null;
  policy = import.meta.env.DEV ? 'warn' : 'silent';
  onMissing = null;
  reported.clear();
  listeners.clear();
  changeListeners.clear();
  readyPromise = new Promise<void>((resolve) => {
    readyResolve = resolve;
  });
}

/** The merged dictionary `t()` reads. Tests and debug overlays only. */
export function debugCatalogue(): Catalogue | null {
  return mergedActive;
}
